import { cx } from "@jugl-web/utils";
import { FC, useMemo } from "react";

export interface DefaultCTAVisualPreviewProps {
  /** The default CTA visual ID, e.g. "default-recording-1" or "default-animation-3". */
  id: string;
  /** Resolved URL for the visual asset. */
  src: string;
  className?: string;
}

interface PreviewStrategy {
  playbackRate: number;
  className: string;
}

/**
 * Renders a preset (default) CTA visual as an auto-playing looping video.
 * Recordings play at normal speed with cover fitting; animations play slower
 * with contain fitting so the full asset is visible.
 */
export const DefaultCTAVisualPreview: FC<DefaultCTAVisualPreviewProps> = ({
  id,
  src,
  className,
}) => {
  const strategy = useMemo<PreviewStrategy>(() => {
    const isRecording = id.startsWith("default-recording");

    if (isRecording) {
      return {
        playbackRate: 1,
        className: "object-cover",
      };
    }

    return {
      playbackRate: 0.4,
      className: "object-contain",
    };
  }, [id]);

  return (
    <video
      ref={(el) => {
        if (el) {
          el.playbackRate = strategy.playbackRate;
        }
      }}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      src={src}
      className={className ?? cx("h-full w-full", strategy.className)}
    />
  );
};
